/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */

import * as React from "react";
import { Button, KIND } from "baseui/button";
import { Input } from "baseui/input";
import { BiEdit } from "react-icons/bi";
import { FaCheck } from "react-icons/fa";
import { StatefulTooltip } from "baseui/tooltip";
import { getTooltipOverrides } from "../../DesignSystem/TooltipStyles";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";

export interface ProjectTitleEditorProps {
  projectName: string;
  projectTitleInProgress: string;
  editing: boolean;
  showValidationError: boolean;
  onStartEditing: () => void;
  onUpdate: (value: string) => void;
  onConfirm: () => void;
  onDiscard: () => void;
}

export const ProjectTitleEditor: React.FC<ProjectTitleEditorProps> = ({
  projectName,
  projectTitleInProgress,
  editing,
  showValidationError,
  onStartEditing,
  onUpdate,
  onConfirm,
  onDiscard,
}) => {
  const { currentTheme } = useDexterTheme();

  const buttonOverrides = {
    BaseButton: {
      style: () => ({
        marginLeft: currentTheme.spacing.sm,
        backgroundColor: currentTheme.colors.fileButtonColor,
        color: currentTheme.colors.editButtonColor,
        cursor: "pointer",
      }),
    },
  };

  if (!editing) {
    return (
      <div
        onDoubleClick={onStartEditing}
        data-testid={"title-edit-button"}
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          position: "relative",
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: currentTheme.fontSizes.large,
            fontFamily: currentTheme.fonts.primary,
            color: currentTheme.colors.text,
          }}
        >
          {projectName}
        </div>
        <StatefulTooltip
          content="Edit Project Title"
          placement="bottom"
          showArrow
          overrides={getTooltipOverrides(currentTheme)}
        >
          <Button
            kind={KIND.tertiary}
            size="mini"
            onClick={onStartEditing}
            style={{ marginLeft: currentTheme.spacing.sm }}
            overrides={buttonOverrides}
          >
            <BiEdit size={16} />
          </Button>
        </StatefulTooltip>
      </div>
    );
  } else {
    return (
      <div
        data-testid="mapping-title-input"
        style={{
          display: "flex",
          alignItems: "center",
          gap: currentTheme.spacing.sm,
        }}
      >
        <Input
          autoFocus
          value={projectTitleInProgress}
          type="text"
          size="default"
          overrides={{
            Input: {
              style: {
                height: currentTheme.dimensions.input.height,
                fontSize: currentTheme.fontSizes.large,
                padding: `0 ${currentTheme.spacing.sm}`,
                boxSizing: "border-box",
              },
            },
          }}
          onChange={(e) => onUpdate(e.target.value)}
          onFocus={(e) => e.target.select()}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !showValidationError) {
              onConfirm();
            }
            if (e.key === "Escape") {
              onDiscard();
            }
          }}
        />
        <Button
          kind={KIND.tertiary}
          size="default"
          onClick={onConfirm}
          overrides={buttonOverrides}
        >
          <FaCheck size={16} />
        </Button>
        <Button
          kind={KIND.tertiary}
          size="default"
          onClick={onDiscard}
          overrides={{
            BaseButton: {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              style: () => ({
                color: currentTheme.colors.text,
                backgroundColor: "transparent",
                ":hover": {
                  backgroundColor:
                    currentTheme.colors.dropdownItemHoverBackground,
                },
              }),
            },
          }}
        >
          Cancel
        </Button>
      </div>
    );
  }
};
